import { type AcompteFacture, type BaseTva, lignesDeduction } from '@imp/shared/acompte';
import { adresseDeFacturation, manquesAdresse } from '@imp/shared/controle-emission';
import type { DevisRattache, FactureRattachee } from '@imp/shared/suivi-facturation';
import { remboursementSaisi } from '@imp/shared/saisie-montant';

import { totalOrigineAcompte } from '@/lib/acompte-origine';
import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import { listTauxTva } from '@/modules/catalogue/api';
import {
  computeDevisTotals,
  type DevisPdfContact,
  type DevisPdfLigne,
  type FormLigne,
  getDevis,
} from '@/modules/devis/api';

export const ORG_ID = '00000000-0000-0000-0000-000000000001';

export const FACTURE_STATUT_LABELS: Record<string, string> = {
  brouillon: 'Brouillon',
  emise: 'Émise',
  envoyee: 'Envoyée',
  partiellement_payee: 'Partiellement payée',
  payee: 'Payée',
  en_retard: 'En retard',
  litige: 'Litige',
  annulee_avoir: 'Annulée (avoir)',
};

function isoDay(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 10);
}

export interface FactureListItem {
  id: string;
  numero: string;
  kind: string;
  statut: string;
  objet: string;
  date_emission: string;
  date_echeance: string;
  montant_ttc: string;
  montant_paye: string;
  contact_id: string;
}

export interface FactureLigneRow {
  id: string;
  libelle: string;
  description: string | null;
  quantite: string;
  unite_id: string | null;
  prix_unitaire_ht: string;
  remise_pourcentage: string;
  taux_tva_id: string | null;
  total_ht: string;
  total_ttc: string;
  ordre: number;
}

/**
 * L'adresse du client telle que figée sur la facture à son émission
 * (`facturation_adresse`). Elle ne bouge plus ensuite, même si la fiche client
 * change : c'est elle, et pas la fiche, qui fait foi.
 */
export interface AdresseFigee {
  ligne1: string;
  ligne2?: string | null;
  codePostal: string;
  ville: string;
  pays?: string | null;
}

export interface FactureDetail {
  id: string;
  numero: string;
  kind: string;
  statut: string;
  contact_id: string;
  devis_id: string | null;
  chantier_id: string | null;
  objet: string;
  date_emission: string;
  date_prestation: string | null;
  date_echeance: string;
  delai_paiement_jours: number;
  montant_ht: string;
  montant_tva: string;
  montant_ttc: string;
  montant_paye: string;
  detail_tva: { tauxCode: string; tauxValeur: number; baseHt: number; montantTva: number }[] | null;
  emise_at: string | null;
  facture_liee_id: string | null;
  metadata: FactureMetadata | null;
  /** `null` sur un brouillon, et sur une facture émise sans adresse figée. */
  facturation_adresse: AdresseFigee | null;
  factures_lignes: FactureLigneRow[];
}

/** Référence d'un document lié, telle que posée par creer_avoir (migration 18). */
export interface RefDocumentLie {
  id: string;
  numero: string;
  date_emission: string;
}

export interface FactureMetadata {
  /** Sur un avoir : la facture qu'il annule. */
  avoir_de?: RefDocumentLie;
  /** Sur une facture annulée : l'avoir qui l'annule. */
  annulee_par?: RefDocumentLie;
  [k: string]: unknown;
}

export async function listFactures(): Promise<FactureListItem[]> {
  // Par tranches : PostgREST plafonne à 1 000 lignes sans le dire.
  return lireTout<FactureListItem>((de, a) =>
    supabase
      .from('factures')
      .select(
        'id,numero,kind,statut,objet,date_emission,date_echeance,montant_ttc,montant_paye,contact_id',
      )
      .order('date_emission', { ascending: false })
      .order('id')
      .range(de, a)
      .overrideTypes<FactureListItem[], { merge: false }>(),
  );
}

export async function getFacture(id: string): Promise<FactureDetail> {
  const { data, error } = await supabase
    .from('factures')
    .select(
      'id,numero,kind,statut,contact_id,devis_id,chantier_id,objet,date_emission,date_prestation,date_echeance,delai_paiement_jours,montant_ht,montant_tva,montant_ttc,montant_paye,detail_tva,emise_at,facture_liee_id,metadata,facturation_adresse,factures_lignes(id,libelle,description,quantite,unite_id,prix_unitaire_ht,remise_pourcentage,taux_tva_id,total_ht,total_ttc,ordre)',
    )
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  const f = data as unknown as FactureDetail;
  f.factures_lignes.sort((a, b) => a.ordre - b.ordre);
  return f;
}

// Convertit un devis en facture BROUILLON (numéro provisoire ; le numéro NF525
// définitif n'est attribué qu'à l'émission).
export async function createFactureFromDevis(devisId: string): Promise<string> {
  const [devis, tvaList, acomptes] = await Promise.all([
    getDevis(devisId),
    listTauxTva(),
    listAcomptesDevis(devisId),
  ]);
  const formLignes: FormLigne[] = devis.devis_lignes.map((l) => ({
    article_id: l.article_id ?? '',
    libelle: l.libelle,
    description: l.description ?? '',
    quantite: String(l.quantite),
    unite_id: l.unite_id ?? '',
    prix_unitaire_ht: String(l.prix_unitaire_ht),
    remise_pourcentage: String(l.remise_pourcentage),
    taux_tva_id: l.taux_tva_id ?? '',
  }));

  // Ce qui a déjà été facturé en acompte se retranche, taux par taux : la
  // facture de solde reprend tout le détail des travaux, puis déduit.
  const parCode = new Map(tvaList.map((t) => [t.code, t.id]));
  for (const d of lignesDeduction(acomptes)) {
    formLignes.push({
      article_id: '',
      libelle: d.libelle,
      description: '',
      quantite: '1',
      unite_id: '',
      prix_unitaire_ht: d.montantHt.toFixed(2),
      remise_pourcentage: '0',
      taux_tva_id: parCode.get(d.tauxCode) ?? '',
    });
  }
  const totals = computeDevisTotals(formLignes, tvaList);

  const { data, error } = await supabase
    .from('factures')
    .insert({
      organization_id: ORG_ID,
      numero: `BROUILLON-${crypto.randomUUID().slice(0, 8)}`,
      kind: acomptes.length > 0 ? 'finale' : 'standard',
      statut: 'brouillon',
      contact_id: devis.contact_id,
      devis_id: devis.id,
      chantier_id: devis.chantier_id,
      objet: acomptes.length > 0 ? `Solde — ${devis.objet}` : devis.objet,
      date_emission: isoDay(0),
      date_echeance: isoDay(30),
      delai_paiement_jours: 30,
      montant_ht: totals.montant_ht.toFixed(2),
      montant_tva: totals.montant_tva.toFixed(2),
      montant_ttc: totals.montant_ttc.toFixed(2),
      detail_tva: totals.detail_tva,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  const factureId = (data as { id: string }).id;

  const lignes = formLignes.map((l, i) => ({
    organization_id: ORG_ID,
    facture_id: factureId,
    ordre: i,
    type: 'article',
    article_id: l.article_id === '' ? null : l.article_id,
    libelle: l.libelle,
    description: l.description.trim() === '' ? null : l.description.trim(),
    quantite: String(Number(l.quantite.replace(',', '.')) || 0),
    unite_id: l.unite_id === '' ? null : l.unite_id,
    prix_unitaire_ht: String(Number(l.prix_unitaire_ht.replace(',', '.')) || 0),
    remise_pourcentage: String(Number(l.remise_pourcentage.replace(',', '.')) || 0),
    taux_tva_id: l.taux_tva_id === '' ? null : l.taux_tva_id,
    total_ht: (totals.ligneTotals[i]?.total_ht ?? 0).toFixed(2),
    total_ttc: (totals.ligneTotals[i]?.total_ttc ?? 0).toFixed(2),
  }));
  if (lignes.length > 0) {
    const ins = await supabase.from('factures_lignes').insert(lignes);
    if (ins.error) throw new Error(ins.error.message);
  }
  return factureId;
}

interface AdresseRow {
  ligne1: string;
  ligne2: string | null;
  code_postal: string;
  ville: string;
  pays: string;
  usage: string;
  est_default: boolean;
  created_at: string;
}

// Émission NF525 : attribue le numéro séquentiel atomique, verrouille la facture
// (immuable via trigger PG) et fige l'adresse de facturation.
export async function emitFacture(id: string): Promise<string> {
  const { data: fac, error: e1 } = await supabase
    .from('factures')
    .select('statut,contact_id,chantier_id,delai_paiement_jours')
    .eq('id', id)
    .single();
  if (e1) throw new Error(e1.message);
  const f = fac as {
    statut: string;
    contact_id: string;
    chantier_id: string | null;
    delai_paiement_jours: number;
  };
  if (f.statut !== 'brouillon') throw new Error('Cette facture est déjà émise.');
  if (f.chantier_id === null) throw new Error(SANS_CHANTIER);

  const { data: numero, error: e2 } = await supabase.rpc('next_document_number', {
    p_type_document: 'facture',
  });
  if (e2) throw new Error(e2.message);

  // La même règle que la base pour un acompte, et que la fiche client : les
  // adresses supprimées sont écartées (elles ne l'étaient pas ici), puis
  // facturation, défaut, la plus ancienne.
  const { data: adrData, error: eAdr } = await supabase
    .from('adresses')
    .select('ligne1,ligne2,code_postal,ville,pays,usage,est_default,created_at')
    .eq('contact_id', f.contact_id)
    .is('deleted_at', null)
    .overrideTypes<AdresseRow[], { merge: false }>();
  if (eAdr) throw new Error(eAdr.message);
  const factu = adresseDeFacturation(adrData);
  // Une facture sans l'adresse du client n'est pas régulière, et une fois émise
  // elle ne se corrige plus : on refuse, tant qu'il est temps de compléter.
  if (manquesAdresse(factu).length > 0) throw new Error(SANS_ADRESSE);
  const facturation_adresse = factu
    ? {
        ligne1: factu.ligne1,
        ligne2: factu.ligne2 ?? undefined,
        codePostal: factu.code_postal,
        ville: factu.ville,
        pays: factu.pays,
      }
    : undefined;

  const { error: e3 } = await supabase
    .from('factures')
    .update({
      numero: numero as unknown as string,
      statut: 'emise',
      emise_at: new Date().toISOString(),
      date_emission: isoDay(0),
      date_echeance: isoDay(f.delai_paiement_jours || 30),
      ...(facturation_adresse ? { facturation_adresse } : {}),
    })
    .eq('id', id);
  if (e3) throw new Error(e3.message);

  return numero as unknown as string;
}

/**
 * Une facture non rattachée à un chantier sort du pilotage : ni marge, ni suivi
 * de chantier, ni chiffre d'affaires rattaché. On refuse de l'émettre, tant
 * qu'il est encore temps de la corriger — après, elle est verrouillée.
 */
/** Refus d'émettre sans adresse complète : la même raison que le récapitulatif. */
export const SANS_ADRESSE =
  'L’adresse du client est absente ou incomplète : complétez-la sur sa fiche avant d’émettre. Une facture émise ne se modifie plus.';

export const SANS_CHANTIER =
  'Rattachez d’abord cette facture à un chantier : sans lui, son chiffre d’affaires ne compte nulle part, et une facture émise ne se modifie plus.';

// Acompte : n'émettre qu'une part du brouillon, le solde plus tard. Tout se
// passe en base, en une transaction (numéro, lignes d'acompte par taux de TVA,
// montants, adresse figée) — voir la migration 21. Renvoie le numéro émis.
export async function emettreAcompte(factureId: string, part: number): Promise<string> {
  const { data: fac } = await supabase
    .from('factures')
    .select('chantier_id')
    .eq('id', factureId)
    .single()
    .overrideTypes<{ chantier_id: string | null }, { merge: false }>();
  if (fac?.chantier_id === null) throw new Error(SANS_CHANTIER);

  const { data, error } = await supabase
    .rpc('emettre_acompte', { p_facture_id: factureId, p_part: part })
    .overrideTypes<string, { merge: false }>();
  if (error) {
    // Le serveur peut être en retard d'une migration : le dire clairement
    // plutôt que de laisser passer un « fonction introuvable » incompréhensible.
    if (
      error.code === 'PGRST202' ||
      /emettre_acompte|does not exist|schema cache/i.test(error.message)
    ) {
      throw new Error(
        'La facture d’acompte demande une mise à jour du serveur (migration 21) : ' +
          'appliquez-la, puis réessayez.',
      );
    }
    throw new Error(error.message);
  }
  if (typeof data !== 'string') throw new Error('Réponse inattendue à l’émission de l’acompte.');
  return data;
}

/**
 * Les acomptes déjà émis sur un devis, pour que la facture de solde les
 * retranche. Les acomptes annulés par un avoir ne comptent plus.
 */
export async function listAcomptesDevis(devisId: string): Promise<AcompteFacture[]> {
  const { data, error } = await supabase
    .from('factures')
    .select('numero,date_emission,detail_tva')
    .eq('devis_id', devisId)
    .eq('kind', 'acompte')
    .neq('statut', 'brouillon')
    .neq('statut', 'annulee_avoir')
    .is('deleted_at', null)
    .order('date_emission')
    .overrideTypes<
      { numero: string; date_emission: string; detail_tva: BaseTva[] | null }[],
      { merge: false }
    >();
  // Un serveur en retard d'une migration n'a pas d'acompte : la facture de
  // solde se comporte alors comme une facture normale, plutôt que d'échouer.
  if (error) return [];
  return data.map((f) => ({
    numero: f.numero,
    dateEmission: f.date_emission.slice(0, 10),
    detailTva: f.detail_tva ?? [],
  }));
}

// Avoir : annulation intégrale d'une facture émise. Tout se passe en base, en
// une transaction (numéro AVO, lignes recopiées, facture passée en
// « annulée (avoir) ») — voir la migration 18. Renvoie l'id de l'avoir.
export async function creerAvoir(factureId: string): Promise<string> {
  const { data, error } = await supabase
    .rpc('creer_avoir', { p_facture_id: factureId })
    .overrideTypes<string, { merge: false }>();
  if (error) throw new Error(error.message);
  if (typeof data !== 'string') throw new Error('Réponse inattendue à la création de l’avoir.');
  return data;
}

// ----- Chargement complet pour le rendu PDF ---------------------------------

export interface FacturePdf {
  id: string;
  numero: string;
  contact_id: string;
  kind: string;
  statut: string;
  metadata: FactureMetadata | null;
  objet: string;
  introduction: string | null;
  date_emission: string;
  date_prestation: string | null;
  date_echeance: string;
  delai_paiement_jours: number;
  montant_ht: string;
  montant_tva: string;
  montant_ttc: string;
  montant_paye: string;
  detail_tva: { tauxCode: string; tauxValeur: number; baseHt: number; montantTva: number }[] | null;
  facturation_adresse: AdresseFigee | null;
  penalites_retard_taux: string;
  indemnite_forfaitaire: string;
  conditions_paiement: string | null;
  mentions_legales: string | null;
  emise_at: string | null;
  contacts: DevisPdfContact | null;
  factures_lignes: DevisPdfLigne[];
}

export async function getFactureForPdf(id: string): Promise<FacturePdf> {
  const { data, error } = await supabase
    .from('factures')
    .select(
      'id,numero,contact_id,kind,statut,metadata,objet,introduction,date_emission,date_prestation,date_echeance,delai_paiement_jours,montant_ht,montant_tva,montant_ttc,montant_paye,detail_tva,facturation_adresse,penalites_retard_taux,indemnite_forfaitaire,conditions_paiement,mentions_legales,emise_at,contacts(type_personne,civilite,nom,prenom,raison_sociale,email,telephone,telephone_mobile,siret,adresses(usage,libelle,ligne1,ligne2,code_postal,ville,est_default)),factures_lignes(id,libelle,description,quantite,prix_unitaire_ht,remise_pourcentage,total_ht,total_ttc,ordre,unites(code),taux_tva(taux,code))',
    )
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  const f = data as unknown as FacturePdf;
  f.factures_lignes.sort((a, b) => a.ordre - b.ordre);
  return f;
}

// ----- Règlements (paiements) -----------------------------------------------

export const REGLEMENT_MODE_LABELS: Record<string, string> = {
  virement: 'Virement',
  cheque: 'Chèque',
  carte: 'Carte',
  especes: 'Espèces',
  prelevement: 'Prélèvement',
  autre: 'Autre',
};

export interface ReglementRow {
  id: string;
  mode: string;
  montant: string;
  date_reglement: string;
  reference: string | null;
}

export async function listReglements(factureId: string): Promise<ReglementRow[]> {
  const { data, error } = await supabase
    .from('reglements')
    .select('id,mode,montant,date_reglement,reference')
    .eq('facture_id', factureId)
    // Un règlement supprimé ne se voit plus et ne compte plus. Sans ce filtre,
    // une erreur de saisie effacée resterait affichée et resterait comptée.
    .is('deleted_at', null)
    .order('date_reglement', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as ReglementRow[];
}

/**
 * Remet `montant_paye` et le statut d'accord avec les règlements RÉELS.
 *
 * Appelée après chaque ajout ET après chaque suppression : deux calculs
 * séparés auraient fini par diverger, et une facture affichée « payée » alors
 * que son règlement a été effacé est le genre d'erreur qu'on ne voit qu'en
 * relançant un client qui a déjà réglé — ou en ne relançant pas celui qui n'a
 * rien payé.
 */
async function recalculerPaiement(factureId: string): Promise<void> {
  const { data: regs, error: regErr } = await supabase
    .from('reglements')
    .select('montant')
    .eq('facture_id', factureId)
    .is('deleted_at', null);
  if (regErr) throw new Error(regErr.message);
  const totalPaye = regs.reduce((s, r) => s + Number(r.montant), 0);

  const { data: facData, error: facErr } = await supabase
    .from('factures')
    .select('montant_ttc,statut')
    .eq('id', factureId)
    .single();
  if (facErr) throw new Error(facErr.message);
  const ttc = Number(facData.montant_ttc);

  const patch: Record<string, unknown> = { montant_paye: totalPaye.toFixed(2) };
  if (totalPaye + 0.005 >= ttc && ttc > 0) {
    patch.statut = 'payee';
    patch.paid_at = new Date().toISOString();
  } else if (totalPaye > 0) {
    patch.statut = 'partiellement_payee';
    patch.paid_at = null;
  } else if (facData.statut === 'payee' || facData.statut === 'partiellement_payee') {
    // Plus rien d'encaissé : la facture redevient une facture envoyée. On ne
    // touche AUCUN autre statut — une facture « en retard » ou « en litige »
    // le reste, ce n'est pas au règlement d'en décider.
    patch.statut = 'envoyee';
    patch.paid_at = null;
  }
  const { error } = await supabase.from('factures').update(patch).eq('id', factureId);
  if (error) throw new Error(error.message);
}

export async function recordReglement(
  factureId: string,
  input: { mode: string; montant: string; date_reglement: string; reference?: string },
): Promise<void> {
  // Dernier rempart : si un montant illisible arrivait jusqu'ici, il ne doit
  // PAS devenir zéro en silence — c'est exactement ce qui a produit trois
  // règlements fantômes de 0,00 € sur la facture de SCI LA FARE.
  // Le signe est l'affaire de l'appelant : positif sur une facture, négatif
  // sur le remboursement d'un avoir. Ici on refuse seulement ce qui ne se lit
  // pas, et le zéro — qui n'est jamais un règlement voulu.
  const montant = remboursementSaisi(input.montant);
  if (montant === null) {
    throw new Error(
      `Montant « ${input.montant} » illisible ou nul : rien n'a été enregistré.`,
    );
  }

  const ins = await supabase.from('reglements').insert({
    organization_id: ORG_ID,
    facture_id: factureId,
    mode: input.mode,
    montant: montant.toFixed(2),
    date_reglement: input.date_reglement,
    reference: input.reference ?? null,
  });
  if (ins.error) throw new Error(ins.error.message);

  await recalculerPaiement(factureId);
}

/**
 * Efface un règlement saisi par erreur, et remet la facture d'aplomb.
 *
 * Suppression DOUCE (`deleted_at`) : la ligne quitte l'écran et les totaux,
 * mais reste en base. Sur une facture, la trace de ce qui a été saisi puis
 * corrigé se garde — c'est ce que demande une comptabilité, et la table est
 * faite pour (elle porte la colonne depuis l'origine).
 */
export async function supprimerReglement(factureId: string, reglementId: string): Promise<void> {
  const { error } = await supabase
    .from('reglements')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', reglementId)
    .is('deleted_at', null);
  if (error) throw new Error(error.message);
  await recalculerPaiement(factureId);
}

export async function markFactureEnvoyee(id: string): Promise<void> {
  const { error } = await supabase
    .from('factures')
    .update({ statut: 'envoyee', envoyee_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// Journalise une relance manuelle (sans envoi d'email automatique).
export async function markFactureRelance(id: string): Promise<void> {
  const { error } = await supabase.from('relances').insert({
    organization_id: ORG_ID,
    facture_id: id,
    niveau: 'relance_1',
    canal: 'manuel',
    contenu: 'Relance enregistrée manuellement',
    automatique: false,
  });
  if (error) throw new Error(error.message);
}

// Rattache une facture à un chantier (autorisé même après émission : chantier_id
// n'est pas un champ verrouillé par le trigger NF525).
export async function setFactureChantier(id: string, chantierId: string | null): Promise<void> {
  const { error } = await supabase
    .from('factures')
    .update({ chantier_id: chantierId })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export interface FacturePilotage {
  id: string;
  chantier_id: string | null;
  montant_ht: string;
  statut: string;
  kind: string;
  date_emission: string;
  /** Pour un avoir : la facture qu'il annule. */
  facture_liee_id: string | null;
}

export async function listFacturesForPilotage(): Promise<FacturePilotage[]> {
  // Lu par tranches : ces factures portent le chiffre d'affaires des chantiers,
  // et PostgREST tronque SANS le dire — une tranche oubliée ferait disparaître
  // du CA et gonflerait une marge, sans la moindre erreur à l'écran.
  return lireTout<FacturePilotage>((de, a) =>
    supabase
      .from('factures')
      .select('id,chantier_id,montant_ht,statut,kind,date_emission,facture_liee_id')
      .not('chantier_id', 'is', null)
      .is('deleted_at', null)
      .order('id')
      .range(de, a),
  );
}

// ─── Contrôle avant émission ────────────────────────────────────────────────

/** Tout ce qu'il faut savoir du chantier pour juger un brouillon. */
export interface ContexteChantier {
  montantTotalHt: number;
  factures: FactureRattachee[];
  devis: DevisRattache[];
}

/**
 * Les pièces du chantier d'une facture, pour savoir si un brouillon dépasse.
 *
 * Lecture seule, et volontairement large : toutes les factures du chantier —
 * brouillons compris, car c'est le brouillon en cours qu'il faut écarter du
 * calcul — et tous ses devis.
 */
export async function getContexteChantier(chantierId: string): Promise<ContexteChantier> {
  const [chantierR, facturesR, devisR] = await Promise.all([
    supabase.from('chantiers').select('montant_total_ht').eq('id', chantierId).single(),
    supabase
      .from('factures')
      .select(
        'id,numero,contact_id,kind,statut,facture_liee_id,montant_ht,montant_ttc,montant_paye,metadata',
      )
      .eq('chantier_id', chantierId)
      .is('deleted_at', null),
    supabase
      .from('devis')
      .select('id,numero,contact_id,statut,montant_ht,montant_ttc')
      .eq('chantier_id', chantierId)
      .is('deleted_at', null),
  ]);
  if (facturesR.error) throw new Error(facturesR.error.message);
  if (devisR.error) throw new Error(devisR.error.message);

  const lignesF = facturesR.data as {
    id: string;
    numero: string | null;
    contact_id: string | null;
    kind: string;
    statut: string;
    facture_liee_id: string | null;
    montant_ht: number | string;
    montant_ttc: number | string;
    montant_paye: number | string;
    metadata: unknown;
  }[];
  const lignesD = devisR.data as {
    id: string;
    numero: string | null;
    contact_id: string | null;
    statut: string;
    montant_ht: number | string;
    montant_ttc: number | string;
  }[];

  return {
    // Une fiche chantier illisible ne doit pas empêcher le contrôle : elle vaut
    // alors 0, et l'engagement se lit sur le devis signé.
    montantTotalHt: Number(
      (chantierR.data as { montant_total_ht?: number | string } | null)?.montant_total_ht ?? 0,
    ),
    factures: lignesF.map((f) => ({
      id: f.id,
      chantierId,
      contactId: f.contact_id,
      numero: f.numero ?? 'brouillon',
      kind: f.kind,
      statut: f.statut,
      factureLieeId: f.facture_liee_id,
      montantHt: Number(f.montant_ht),
      montantTtc: Number(f.montant_ttc),
      montantPaye: Number(f.montant_paye),
      montantOrigineHt: totalOrigineAcompte(f.metadata),
    })),
    devis: lignesD.map((d) => ({
      id: d.id,
      chantierId,
      contactId: d.contact_id,
      numero: d.numero ?? 'brouillon',
      statut: d.statut,
      montantHt: Number(d.montant_ht),
      montantTtc: Number(d.montant_ttc),
    })),
  };
}
